import { useFeaturesLoader } from '@teamwork/use';
import { useIntervalFn } from '@vueuse/core';
import { computed, shallowRef, unref, watch, onScopeDispose } from 'vue-demi';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { useLaunchDarkly } from './useLaunchDarkly';

export default ({ userLocalization, userContext }) => {
  const { items: features } = useFeaturesLoader({
    count: 60,
    pageSize: 100,
  });

  const enabledFeatures = computed(() => {
    return features.value.filter(
      ({ startAt, endAt, scope }) =>
        (startAt === null || isBefore(parseISO(startAt), userLocalization.nowInUtc.value)) &&
        (endAt === null || isAfter(parseISO(endAt), userLocalization.nowInUtc.value)) &&
        (scope !== 'ownercompany' || userContext.user.value.inOwnerCompany) &&
        // scope=='nobody' generally means "disabled in beta, but potentially togglable"
        scope !== 'nobody',
    );
  });

  const { client: ldClient, ready: ldReady, initialized: ldInitialized } = useLaunchDarkly();
  const launchDarklyUpdateCount = shallowRef(0);
  let removeChangeListener;

  const triggerLaunchDarklyUpdate = () => {
    launchDarklyUpdateCount.value += 1;
  };

  watch([ldReady, ldInitialized], triggerLaunchDarklyUpdate);

  const addChangeListener = () => {
    const client = ldClient.value;
    if (client) {
      client.on('change', triggerLaunchDarklyUpdate);
      return () => client.off('change', triggerLaunchDarklyUpdate);
    }
    return undefined;
  };

  watch(
    ldClient,
    () => {
      removeChangeListener?.();
      removeChangeListener = addChangeListener;
    },
    { immediate: true },
  );

  // Make sure that the calls to the LaunchDarkly `track` function are recorded,
  // even if the app keeps running for more than 24 hours.
  // See https://docs.launchdarkly.com/home/experimentation/interpreting#understanding-conversions
  useIntervalFn(triggerLaunchDarklyUpdate, 24 * 60 * 60 * 1000);

  onScopeDispose(() => {
    removeChangeListener?.();
    removeChangeListener = undefined;
  });

  /**
   * @template {any} [T=boolean] flagType
   * @param {string} name
   * @param {T} defaultValue
   * @param {T} overrideValue
   * @returns {import('vue-demi').ComputedRef<T>} a `computed` which provides a feature flag value from LaunchDarkly.
   */
  const ldFlag = (name, defaultValue, overrideValue) => {
    return computed(() => {
      if (unref(overrideValue) !== undefined) {
        return unref(overrideValue);
      }
      // Accessing `launchDarklyUpdateCount.value` retriggers the computed when the flag
      // value must be retrieved from LaunchDarkly again.
      if (ldClient.value && launchDarklyUpdateCount.value >= 0) {
        return ldClient.value.variation(name, unref(defaultValue));
      }

      return unref(defaultValue);
    });
  };

  const getFeatureValue = (featureKey) => {
    return enabledFeatures.value.find(({ key }) => key === featureKey)?.value;
  };

  const isFeatureEnabled = (featureKey) =>
    enabledFeatures.value.some(({ key }) => key === featureKey);

  // LaunchDarkly flags
  const projectsWorkflowsEnabled = ldFlag('projects-workflows', false);
  const projectsGlobalWorkflowsEnabled = ldFlag('projects-global-workflows', false);

  return {
    enabledFeatures,
    isFeatureEnabled,
    getFeatureValue,
    // Being returned to stop unused error, expected use would be to return `someFlag: ldFlag('someFlag', false)`
    ldFlag,
    projectsWorkflowsEnabled,
    projectsGlobalWorkflowsEnabled,
  };
};
